import {
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
} from "@mui/material";
import { Button } from "components/Button/Button";
import ImageLoader from "components/ImageLoader/ImageLoader";
import ModalBox from "components/modal/ModalBox";
import { encryptData } from "config/config";
import useCookie from "hooks/useCookie";
import useDebounce from "hooks/useDebounce";
import useMuiButtonLoader from "hooks/useMuiButtonLoader";
import useStepper from "hooks/useStepper";
import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import Pagination from "react-js-pagination";
import {
  bulkAssignUserToCourse,
  getCoursesByStudent,
} from "services/student/studentController";

import bulkAssignExcelFile from "assets/excels/BulkAssignCourseUpload.xlsx";

const BulkCourseModal = ({ open, onClose }) => {
  const [search, setSearch] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState("");
  const inputRef = useRef(null);

  const { cookies } = useCookie();
  const debouncedValue = useDebounce(search, 300);

  const [submitButtonElement, setSubmitLoading] = useMuiButtonLoader();

  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [flag, setFlag] = useState(false);

  // stepper hooks
  const { activeStep, Stepper, handleNext, handleBack, allStepsCompleted } =
    useStepper(["Select Course", "Select File"]);

  const courseSelectionHandler = (id) => {
    setSelectedCourse(id);
  };

  const fileSelectionHandler = (e) => {
    const file = e.target.files[0];
    // file type validation
    if (!file?.name) {
      toast.error("Please select file");
      return;
    }
    setBulkFile(file);
  };

  // course assign handler
  const assignHandler = async () => {
    if (activeStep === 0 && selectedCourse) {
      handleNext();
    }
    if (activeStep === 1) {
      // main upload file
      try {
        setSubmitLoading(true);
        setFlag(true);
        const formData = new FormData();
        const payloadData = {
          course_id: selectedCourse,
          access_token: cookies,
        };
        formData.append("reqData", encryptData(payloadData));
        formData.append("excelFile", bulkFile);
        const options = {
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            let percent = Math.floor((loaded * 100) / total);
            setUploadPercentage(percent);
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
        };
        const res = await bulkAssignUserToCourse(formData, options);

        if (res.code === 200) {
          toast.success(res.message);
          setSearch("");
          setBulkFile("");
          setSelectedCourse("");
          setSubmitLoading(false);
          setFlag(false);
          onClose();
        } else {
          toast.error(res.message);
          setSubmitLoading(false);
          setFlag(false);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message);
        setSubmitLoading(false);
        setFlag(false);
      }
    }
  };

  const modalOnClose = () => {
    setSearch("");
    setBulkFile("");
    setSelectedCourse("");
    onClose();
  };

  //   get all courses
  const getAllCourses = async (page = activePage, search = debouncedValue) => {
    setLoading(true);
    try {
      const res = await getCoursesByStudent({
        // studentId: userData?.UserID,
        page,
        search,
      });

      if (res.code === 200) {
        setAllCourses(res.data.userCourses || []);
        setTotal(res.data.count || 0);
        setLoading(false);
      } else {
        toast.error(res.message);
        setLoading(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      getAllCourses();
    }
    // eslint-disable-next-line
  }, [debouncedValue, open, activePage]);

  return (
    <>
      <ModalBox
        size={"sm"}
        status={open}
        onClose={modalOnClose}
        closeIcon
        title={`Bulk Assign Course`}
        custom
        customizedFooter={
          <div>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              className="mx-3"
            >
              Back
            </Button>
            <Button
              className="mx-2"
              onClick={() => window.open(bulkAssignExcelFile)}
            >
              Download Sample
            </Button>

            {allStepsCompleted() ? (
              <Button
                onClick={assignHandler}
                disabled={!bulkFile?.name}
                className="mx-3"
              >
                Upload
              </Button>
            ) : (
              <Button
                onClick={assignHandler}
                disabled={!selectedCourse}
                className="mx-3"
              >
                Next
              </Button>
            )}
          </div>
        }
      >
        <div>
          <div className="stepper my-3">
            <div className="w-50 mx-auto">
              <Stepper />
            </div>
          </div>

          {/* course selection list */}
          {activeStep === 0 && (
            <div className="form-group mt-1 row">
              <div className="div_wrap my-3">
                <input
                  type="text"
                  placeholder="Search"
                  className="form-control"
                  name="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/*  loading section */}
              {loading && <LinearProgress />}

              {/* course list section */}
              {!loading && (
                <div className="TableList overflow-auto table-responsive py-0">
                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                    }}
                  >
                    {allCourses.length > 0 ? (
                      allCourses.map(({ CourseID, CourseTitle }, i) => (
                        <ListItem
                          key={CourseID}
                          disablePadding
                          className="border"
                        >
                          <ListItemButton
                            role={undefined}
                            onClick={() => courseSelectionHandler(CourseID)}
                            dense
                          >
                            <ListItemIcon>
                              <Radio
                                edge="start"
                                checked={selectedCourse === CourseID}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-labelledby": CourseID }}
                              />
                            </ListItemIcon>
                            <ListItemText id={CourseID} primary={CourseTitle} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disablePadding>
                        <ListItemButton
                          role={undefined}
                          // onClick={handleToggle(value)}
                          dense
                        >
                          <h5 className="w-100 text-center">
                            Entries Not Available
                          </h5>
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                  {total > 10 ? (
                    <div className="Pagination d-flex justify-content-center m-0">
                      <Pagination
                        activePage={activePage}
                        previousLabel={"previous"}
                        nextLabel={"next"}
                        itemsCountPerPage={10}
                        totalItemsCount={total}
                        pageRangeDisplayed={5}
                        onChange={(page) => setActivePage(page)}
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* file upload section */}
          {activeStep === 1 && (
            <div className="row">
              <div className="col-sm-12 text-center">
                <div id="FileUpload">
                  <input
                    className="d-none"
                    type="file"
                    name="upload-file"
                    ref={inputRef}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={fileSelectionHandler}
                  />
                  <div className="upload-wrapper">
                    {flag ? (
                      <ImageLoader progress={uploadPercentage} />
                    ) : (
                      <div className={`upload `}>
                        <p>Select&nbsp;File</p>
                        <h6>{bulkFile?.name}</h6>

                        <button
                          className="upload__button"
                          type="button"
                          onClick={() => inputRef.current?.click()}
                          disabled={submitButtonElement.disabled}
                        >
                          Select
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalBox>
    </>
  );
};

export default BulkCourseModal;
